// MobileHome — tela Início da superfície MOBILE (touch). Parede A3.
//
// Scroll vertical nativo com RAILS horizontais dentro. Reusa a MESMA camada
// de dados da HomeScreen da TV (queries React Query sobre `api` + `local-db`,
// keys scopadas por provider/perfil via useProviderQueryKey/useScopedQueryKey).
// SÓ a apresentação muda: nada de Norigin/foco; hero ESTÁTICO e MUDO (poster +
// botão play tapável, sem áudio/autoplay); cards via MobileVodCard.
//
// Estado "sem fonte" (no_provider): a conta Swipe é válida mas não há fonte
// Xtream ativa → o backend devolve 401 no_provider. Em vez de quebrar, a Home
// mostra um CTA "configure sua fonte" (placeholder por ora — o fluxo completo
// é parede posterior). Detecção: qualquer uma das queries de catálogo falhou
// com code 'no_provider'.

import { useTranslation } from '@app-iptv/i18n'
import { useQuery } from '@tanstack/react-query'
import { useMemo } from 'react'

import { api, ApiError } from '../api'
import { Poster, rewriteImgUrl } from '../components/Poster'
import {
  useProviderQueryKey,
  useScopedQueryKey,
} from '../hooks/useScopedQueryKey'
import { cssUrl } from '../lib/css-url'
import {
  getFavorites,
  getRecentPositions,
  type Favorite,
  type PlaybackPosition,
} from '../local-db'

import styles from './MobileHome.module.css'
import { MobileRail } from './MobileRail'
import { MobileVodCard } from './MobileVodCard'

/** O que o card precisa pra abrir o player (A4) / detalhe (A5). title/poster
 *  são opcionais (vêm do card pra o player não "piscar"); resumeFrom carrega o
 *  ponto de "continuar assistindo" quando o tap veio do rail de posições. */
export interface MobileHomeItem {
  id: string
  kind: 'vod' | 'series' | 'channel'
  title?: string | undefined
  poster?: string | null | undefined
  resumeFrom?: number | undefined
}

export interface MobileHomeProps {
  /** Tap num card ou no play do hero. Em A3 o shell trata como placeholder. */
  onOpenItem: (item: MobileHomeItem) => void
  /** Tap no CTA "configure sua fonte" (estado no_provider). */
  onConfigureSource?: (() => void) | undefined
  /** Tap no atalho "gerenciar perfis" (estado no_provider) — abre o Perfil/Ajustes
   *  pra deixar a conta+perfis acessíveis logo após o login, sem opção escondida. */
  onOpenProfile?: (() => void) | undefined
}

/** True se o erro da query é o 401 no_provider (conta válida, sem fonte). */
function isNoProvider(err: unknown): boolean {
  return err instanceof ApiError && err.code === 'no_provider'
}

export function MobileHome({
  onOpenItem,
  onConfigureSource,
  onOpenProfile,
}: MobileHomeProps): JSX.Element {
  const { t } = useTranslation()

  // ===== Dados locais (IndexedDB via React Query) — mesmas keys da TV ===== //
  const positionsQ = useQuery({
    queryKey: useScopedQueryKey(['home-positions']),
    queryFn: () => getRecentPositions(20),
    staleTime: 60 * 1000,
  })
  const favoritesQ = useQuery({
    queryKey: useScopedQueryKey(['favorites', 'all']),
    queryFn: () => getFavorites(),
    staleTime: 60 * 1000,
  })

  // ===== Catálogo (backend) — mesmas keys/queries que a HomeScreen TV ===== //
  const moviesQ = useQuery({
    queryKey: useProviderQueryKey(['vod-recent', 50]),
    queryFn: () => api.getRecentVod(50),
    staleTime: 10 * 60 * 1000,
    // no_provider não é erro recuperável por retry: a conta só não tem fonte.
    retry: (count, err) => !isNoProvider(err) && count < 2,
  })
  const seriesQ = useQuery({
    queryKey: useProviderQueryKey(['series-recent', 50]),
    queryFn: () => api.getRecentSeries(50),
    staleTime: 10 * 60 * 1000,
    retry: (count, err) => !isNoProvider(err) && count < 2,
  })

  const positions = useMemo(() => positionsQ.data ?? [], [positionsQ.data])
  const favorites = useMemo(() => favoritesQ.data ?? [], [favoritesQ.data])
  // Canais ficam fora da vitrine de favoritos (poster vertical vs logo).
  const favoritesVodSeries = useMemo(
    () => favorites.filter((f) => f.type !== 'channel'),
    [favorites],
  )
  const newMovies = useMemo(() => moviesQ.data ?? [], [moviesQ.data])
  const newSeries = useMemo(() => seriesQ.data ?? [], [seriesQ.data])

  // Hero estático/mudo: 1º filme com poster, senão 1ª série com poster.
  const hero = useMemo(() => {
    const m = newMovies.find((v) => v.poster && v.name)
    if (m) return { id: m.id, name: m.name, poster: m.poster, kind: 'vod' as const }
    const s = newSeries.find((v) => v.poster && v.name)
    if (s) return { id: s.id, name: s.name, poster: s.poster, kind: 'series' as const }
    return null
  }, [newMovies, newSeries])

  // ===== Estados de borda ===== //
  const noProvider =
    isNoProvider(moviesQ.error) || isNoProvider(seriesQ.error)

  const catalogLoading = moviesQ.isLoading || seriesQ.isLoading
  const hasAnything =
    positions.length > 0 ||
    favoritesVodSeries.length > 0 ||
    newMovies.length > 0 ||
    newSeries.length > 0

  // Sem fonte → tela de boas-vindas com OS DOIS caminhos visíveis (sem becos):
  // CTA primário "configurar fonte" + atalho secundário pra "gerenciar perfis".
  // Não é crash; tem prioridade sobre loading/empty.
  if (noProvider) {
    return (
      <div className={styles.stateWrap} data-state="no-provider">
        <h1 className={styles.stateTitle}>{t('mobileHome.noSourceTitle')}</h1>
        <p className={styles.stateText}>{t('mobileHome.noSourceText')}</p>
        <div className={styles.stateActions}>
          {onConfigureSource && (
            <button
              type="button"
              className={styles.stateCta}
              data-action="configure-source"
              onClick={onConfigureSource}
            >
              {t('mobileHome.noSourceCta')}
            </button>
          )}
          {onOpenProfile && (
            <button
              type="button"
              className={styles.stateLink}
              data-action="open-profile"
              onClick={onOpenProfile}
            >
              {t('mobileHome.noSourceProfile')}
            </button>
          )}
        </div>
      </div>
    )
  }

  // Carregando o catálogo e ainda sem nada local pra pintar → skeleton/spinner.
  if (catalogLoading && !hasAnything) {
    return (
      <div className={styles.stateWrap} data-state="loading">
        <div className={styles.spinner} aria-hidden="true" />
        <p className={styles.stateText}>{t('mobileHome.loading')}</p>
      </div>
    )
  }

  return (
    <div className={styles.home} data-state="ready">
      {/* HERO estático e MUDO — poster + play tap (sem áudio/autoplay). */}
      {hero && (
        <div className={styles.hero}>
          <div
            className={styles.heroBackdrop}
            style={{
              backgroundImage: cssUrl(rewriteImgUrl(hero.poster, { kind: 'backdrop' })),
            }}
            aria-hidden="true"
          />
          <div className={styles.heroScrim} aria-hidden="true" />
          <div className={styles.heroPosterWrap}>
            <Poster
              src={hero.poster}
              kind="poster-large"
              alt={hero.name}
              fallbackText={hero.name}
              className={styles.heroPoster}
              placeholderClassName={`${styles.heroPoster} ${styles.heroPosterPh}`}
            />
          </div>
          <div className={styles.heroInfo}>
            <h1 className={styles.heroTitle}>{hero.name}</h1>
            <button
              type="button"
              className={styles.heroPlay}
              onClick={() =>
                onOpenItem({ id: hero.id, kind: hero.kind, title: hero.name, poster: hero.poster })
              }
            >
              ▶ {t('mobileHome.play')}
            </button>
          </div>
        </div>
      )}

      <div className={styles.rails}>
        {/* CONTINUAR ASSISTINDO */}
        {positions.length > 0 && (
          <MobileRail railId="continue" title={t('home.continueWatching')}>
            {positions.map((p: PlaybackPosition) => {
              const pct = p.durationSec > 0 ? (p.positionSec / p.durationSec) * 100 : 0
              const kind: 'vod' | 'series' = p.type === 'vod' ? 'vod' : 'series'
              const itemId = p.type === 'series-episode' && p.seriesId ? p.seriesId : p.itemId
              return (
                <MobileVodCard
                  key={p.id}
                  title={p.title}
                  poster={p.poster}
                  progress={pct}
                  kind={kind}
                  onPress={() =>
                    onOpenItem({
                      id: itemId,
                      kind,
                      title: p.title,
                      poster: p.poster,
                      // Retoma só p/ VOD: episódio de série precisa de season/
                      // episode (vem do detalhe da série, A5).
                      ...(kind === 'vod' ? { resumeFrom: p.positionSec } : {}),
                    })
                  }
                />
              )
            })}
          </MobileRail>
        )}

        {/* FAVORITOS */}
        {favoritesVodSeries.length > 0 && (
          <MobileRail railId="favorites" title={t('home.favorites')}>
            {favoritesVodSeries.map((f: Favorite) => {
              const kind: 'vod' | 'series' = f.type === 'series' ? 'series' : 'vod'
              return (
                <MobileVodCard
                  key={f.id}
                  title={f.title}
                  poster={f.poster}
                  kind={kind}
                  onPress={() =>
                    onOpenItem({ id: f.itemId, kind, title: f.title, poster: f.poster })
                  }
                />
              )
            })}
          </MobileRail>
        )}

        {/* FILMES LANÇAMENTOS */}
        {newMovies.length > 0 && (
          <MobileRail railId="movies-new" title={t('home.moviesNew')}>
            {newMovies.map((m) => (
              <MobileVodCard
                key={m.id}
                title={m.name}
                poster={m.poster}
                year={m.year}
                kind="vod"
                onPress={() => onOpenItem({ id: m.id, kind: 'vod', title: m.name, poster: m.poster })}
              />
            ))}
          </MobileRail>
        )}

        {/* SÉRIES RECÉM ATUALIZADAS */}
        {newSeries.length > 0 && (
          <MobileRail railId="series-new" title={t('home.seriesNew')}>
            {newSeries.map((s) => (
              <MobileVodCard
                key={s.id}
                title={s.name}
                poster={s.poster}
                kind="series"
                onPress={() => onOpenItem({ id: s.id, kind: 'series', title: s.name, poster: s.poster })}
              />
            ))}
          </MobileRail>
        )}
      </div>

      {/* Empty state — conta com fonte, mas catálogo vazio e nada local. */}
      {!hero && !hasAnything && !catalogLoading && (
        <div className={styles.stateWrap} data-state="empty">
          <p className={styles.stateText}>{t('home.empty')}</p>
        </div>
      )}
    </div>
  )
}
